import { styled } from '@mui/system';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

export const StyledAccordion = styled(MuiAccordion)``;
export const StyledAccordionSummary = styled(MuiAccordionSummary)`
  & .MuiAccordionSummary-expandIconWrapper {
    transform: rotate(-180deg);
  }

  & .MuiAccordionSummary-expandIconWrapper.Mui-expanded {
    transform: rotate(0deg);
  }
`;
export const StyledCaptionAccordionSummary = styled(StyledAccordionSummary)`
  & .MuiAccordionSummary-content {
    margin: 10px 0;
    min-width: 0;
  }

  &.Mui-expanded .MuiAccordionSummary-content {
    margin: 10px 0;
  }
`;

type AccordionProps<T extends string | number> = {
  expandedPanelName?: T;
  panelName: T;
  onChange: (panel: T) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  children: React.ReactNode;
  allOpenValue?: T;
};

export const Accordion = <T extends string | number>({
  expandedPanelName,
  panelName,
  onChange,
  children,
  allOpenValue,
}: AccordionProps<T>) => {
  const isExpanded = expandedPanelName === panelName || (allOpenValue && expandedPanelName === allOpenValue);
  return (
    <StyledAccordion expanded={isExpanded} onChange={onChange(panelName)}>
      {children!}
    </StyledAccordion>
  );
};

export enum AccordionSummaryVariant {
  DEFAULT = 'DEFAULT',
  CAPTION = 'CAPTION',
}

type AccordionSummaryProps = {
  title?: string;
  summaryComponent?: React.ReactNode;
  actionComponent?: React.ReactNode;
  variant?: AccordionSummaryVariant;
};
export const AccordionSummary = ({
  title,
  summaryComponent,
  actionComponent,
  variant = AccordionSummaryVariant.DEFAULT,
}: AccordionSummaryProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  if (variant === AccordionSummaryVariant.CAPTION) {
    return (
      <StyledCaptionAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container direction="column" gap={0.5} sx={{ width: '100%', minWidth: 0 }}>
          <Grid container alignItems="center" gap={0.5} wrap="nowrap">
            <Typography
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: 400,
                letterSpacing: 0,
                color: 'text.secondary',
                lineHeight: 1.2,
              }}>
              {title}
            </Typography>
            {actionComponent}
          </Grid>
          {summaryComponent && (
            <Grid
              sx={{
                width: '100%',
                minWidth: 0,
                color: 'text.primary',
                fontSize: '16px',
                '& .MuiTypography-body1': { fontSize: '16px', fontWeight: 500 },
              }}>
              {summaryComponent}
            </Grid>
          )}
        </Grid>
      </StyledCaptionAccordionSummary>
    );
  }
  return (
    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Grid
        container
        flexDirection={isSmallScreen ? 'column' : 'row'}
        alignItems={isSmallScreen ? undefined : 'center'}
        justifyContent="space-between">
        <Grid item display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 500 }}>
            {title}
          </Typography>
          {actionComponent}
        </Grid>
        {summaryComponent && (
          <Grid item mx={isSmallScreen ? 0 : 1} justifyContent="flex-end" color="text.secondary">
            {summaryComponent}
          </Grid>
        )}
      </Grid>
    </StyledAccordionSummary>
  );
};
